import { c as _c } from "react/compiler-runtime";
import { type BusinessVaultFormData, businessDiToFormData, createFieldErrorHandler } from '@onefootprint/core';
import type { FieldError } from '@onefootprint/hooks';
import type { Language, Locale, ModernBusinessDecryptResponse, ModernRawBusinessDataRequest, OnboardingRequirementCollectBusinessData } from '@onefootprint/request-types';
import { Divider, PageShell } from '@onefootprint/ui';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { StepUpContext } from '@/idv2/hooks/use-step-up';
import { BasicDataFields } from './components/basic-data-fields';
import { BusinessAddress } from './components/business-address';
import { fromVaultData, toVaultData } from './utils/format-vault-data';
import { getAllAttributes } from './utils/get-all-attributes';
export type CollectBusinessDataProps = {
  currentRequirement: OnboardingRequirementCollectBusinessData;
  decryptedVault: ModernBusinessDecryptResponse;
  isLoading: boolean;
  onSubmit: (data: ModernRawBusinessDataRequest, onError: (errors: FieldError[]) => void) => void;
  l10n: {
    locale?: Locale;
    language: Language;
  };
  authToken: string;
  stepUpContext: StepUpContext;
};
export const CollectBusinessData = t0 => {
  const $ = _c(49);
  const {
    currentRequirement,
    decryptedVault,
    isLoading,
    onSubmit,
    l10n,
    authToken,
    stepUpContext
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "collectBusinessData"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("idv2", t1);
  let attributes;
  let t2;
  if ($[1] !== currentRequirement) {
    attributes = getAllAttributes(currentRequirement);
    t2 = attributes.includes("business_address");
    $[1] = currentRequirement;
    $[2] = attributes;
    $[3] = t2;
  } else {
    attributes = $[2];
    t2 = $[3];
  }
  const showBusinessAddress = t2;
  const isTinScrubbed = decryptedVault["business.tin"] === "scrubbed";
  let t3;
  if ($[4] !== decryptedVault || $[5] !== isTinScrubbed) {
    t3 = isTinScrubbed ? {
      ...decryptedVault,
      "business.tin": undefined
    } : decryptedVault;
    $[4] = decryptedVault;
    $[5] = isTinScrubbed;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const vaultDataForForm = t3;
  let t4;
  if ($[7] !== vaultDataForForm) {
    t4 = fromVaultData(vaultDataForForm);
    $[7] = vaultDataForForm;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = {
      defaultValues: t4,
      mode: "onTouched"
    };
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const methods = useForm(t5);
  let T0;
  let t6;
  let t7;
  if ($[11] !== methods || $[12] !== onSubmit) {
    const handleFieldErrors = createFieldErrorHandler(methods, {
      keyToFieldName: businessDiToFormData
    });
    const handleSubmit = data => {
      const vaultData = toVaultData(data);
      onSubmit(vaultData, handleFieldErrors);
    };
    T0 = FormProvider;
    t7 = methods;
    t6 = methods.handleSubmit(handleSubmit);
    $[11] = methods;
    $[12] = onSubmit;
    $[13] = T0;
    $[14] = t6;
    $[15] = t7;
  } else {
    T0 = $[13];
    t6 = $[14];
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t) {
    t8 = t(_temp);
    $[16] = t;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t) {
    t9 = t(_temp2);
    $[18] = t;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t) {
    t10 = t(_temp3);
    $[20] = t;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10) {
    t11 = {
      text: t10,
      type: "submit"
    };
    $[22] = t10;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const t12 = l10n.locale ?? "en-US";
  let t13;
  if ($[24] !== attributes || $[25] !== authToken || $[26] !== isTinScrubbed || $[27] !== stepUpContext || $[28] !== t12) {
    t13 = <BasicDataFields attributes={attributes} locale={t12} isTinScrubbed={isTinScrubbed} authToken={authToken} stepUpContext={stepUpContext} />;
    $[24] = attributes;
    $[25] = authToken;
    $[26] = isTinScrubbed;
    $[27] = stepUpContext;
    $[28] = t12;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== l10n.locale || $[31] !== showBusinessAddress) {
    t14 = showBusinessAddress && <><Divider /><BusinessAddress locale={l10n.locale ?? "en-US"} /></>;
    $[30] = l10n.locale;
    $[31] = showBusinessAddress;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== t13 || $[34] !== t14) {
    t15 = <div className="flex flex-col gap-6 w-full">{t13}{t14}</div>;
    $[33] = t13;
    $[34] = t14;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== isLoading || $[37] !== t11 || $[38] !== t15 || $[39] !== t8 || $[40] !== t9) {
    t16 = <PageShell title={t8} subtitle={t9} cta={t11} isLoading={isLoading}>{t15}</PageShell>;
    $[36] = isLoading;
    $[37] = t11;
    $[38] = t15;
    $[39] = t8;
    $[40] = t9;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== t16 || $[43] !== t6) {
    t17 = <form onSubmit={t6}>{t16}</form>;
    $[42] = t16;
    $[43] = t6;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== T0 || $[46] !== t17 || $[47] !== t7) {
    t18 = <T0 {...t7}>{t17}</T0>;
    $[45] = T0;
    $[46] = t17;
    $[47] = t7;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  return t18;
};
function _temp($) {
  return $.pages.basicData.title;
}
function _temp2($_0) {
  return $_0.pages.basicData.subtitle;
}
function _temp3($_1) {
  return $_1.pages.basicData.cta;
}